import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Fab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  Forum,
  Lock,
  EmojiEvents,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTermsStatus, usePosts } from '../../hooks/useCommunity';
import { useTogglePostPin, useHidePost, useUnhidePost, useHideComment } from '../../hooks/useCommunityMutations';
import {
  TermsAcceptanceModal,
  PostCard,
  CreatePostModal,
  PostDetailModal,
  ReportDialog,
  AdminModerationPanel,
} from '../../components/community';
import type { CommunityPost, CommunityComment, PostFilters } from '../../types/community';
import { COLORS } from '@elevair/shared';

export default function CommunityHubPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, organization } = useAuth();

  // State
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [createPostModalOpen, setCreatePostModalOpen] = useState(false);
  const [editPost, setEditPost] = useState<CommunityPost | null>(null);
  const [selectedPost, setSelectedPost] = useState<CommunityPost | null>(null);
  const [reportTarget, setReportTarget] = useState<{
    target: CommunityPost | CommunityComment;
    type: 'post' | 'comment';
  } | null>(null);
  const [filters] = useState<PostFilters>({ page: 1, limit: 20 });

  // Check tier access with fallback for trial users
  let tier = organization?.subscription?.tier || 'free';
  // Fallback: If tier is 'free' but status indicates trial, treat as trial
  if (tier === 'free' && (organization?.subscription?.status === 'trialing' || organization?.subscription?.status === 'trial')) {
    tier = 'trial';
  }
  // Fallback: If tier is 'free' but trialEndsAt is in the future, treat as trial
  if (tier === 'free' && organization?.subscription?.trialEndsAt && new Date(organization.subscription.trialEndsAt) > new Date()) {
    tier = 'trial';
  }
  // Trial and all paid tiers can access Community Hub
  const hasHubAccess = tier === 'trial' || tier === 'launch' || tier === 'rise' || tier === 'summit';
  const isSuperAdmin = user?.role === 'super_admin';
  // Write access requires Launch+ tier (or super admin) - Trial is read-only
  const canWriteToHub = isSuperAdmin || tier === 'launch' || tier === 'rise' || tier === 'summit';
  const isReadOnly = hasHubAccess && !canWriteToHub; // Trial tier is read-only

  // Queries
  const { data: termsStatus, isLoading: loadingTerms } = useTermsStatus();
  const { data: postsData, isLoading: loadingPosts, isError: postsError, refetch: refetchPosts } = usePosts(filters);

  // Mutations
  const togglePin = useTogglePostPin();
  const hidePost = useHidePost();
  const unhidePost = useUnhidePost();
  const hideComment = useHideComment();

  const hasAcceptedTerms = termsStatus?.data?.hasAcceptedCurrentVersion;
  const posts = postsData?.data?.posts || [];

  // Show terms modal if user hasn't accepted (for users with hub access)
  useEffect(() => {
    if (hasHubAccess && !loadingTerms && !hasAcceptedTerms && termsStatus) {
      setTermsModalOpen(true);
    }
  }, [hasHubAccess, loadingTerms, hasAcceptedTerms, termsStatus]);

  const handleTermsAccepted = () => {
    setTermsModalOpen(false);
  };

  const handleTermsDeclined = () => {
    setTermsModalOpen(false);
    navigate('/dashboard');
  };

  const handleEditPost = (post: CommunityPost) => {
    setEditPost(post);
    setCreatePostModalOpen(true);
  };

  const handleReportPost = (post: CommunityPost) => {
    setReportTarget({ target: post, type: 'post' });
  };

  const handleReportComment = (comment: CommunityComment) => {
    setReportTarget({ target: comment, type: 'comment' });
  };

  const handleCommentClick = (post: CommunityPost) => {
    setSelectedPost(post);
  };

  const handlePinPost = async (postId: string, pinned: boolean) => {
    try {
      await togglePin.mutateAsync({ id: postId, pinned });
      refetchPosts();
    } catch (error) {
      console.error('Failed to toggle pin:', error);
    }
  };

  const handleHidePost = async (postId: string) => {
    try {
      await hidePost.mutateAsync({ id: postId, reason: 'Hidden by moderator' });
      refetchPosts();
    } catch (error) {
      console.error('Failed to hide post:', error);
    }
  };

  const handleUnhidePost = async (postId: string) => {
    try {
      await unhidePost.mutateAsync(postId);
      refetchPosts();
    } catch (error) {
      console.error('Failed to unhide post:', error);
    }
  };

  const handleHideComment = async (commentId: string) => {
    try {
      await hideComment.mutateAsync({ id: commentId, reason: 'Hidden by moderator' });
    } catch (error) {
      console.error('Failed to hide comment:', error);
    }
  };

  // Show upgrade prompt for users without hub access (free/expired tier only)
  if (!hasHubAccess && !isSuperAdmin) {
    return (
      <Box>
        <Box mb={4}>
          <Typography variant="h4" fontWeight="bold">
            Community Hub
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Connect with fellow professionals
          </Typography>
        </Box>

        <Card sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: `${COLORS.accent}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <Lock sx={{ fontSize: 40, color: COLORS.accent }} />
            </Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Upgrade to Access Community Hub
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Join a vibrant community of service professionals! Share tips, ask questions,
              and grow together with fellow professionals.
            </Typography>
            <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap" mb={4}>
              <Chip icon={<Forum />} label="Discussion Feed" />
              <Chip icon={<EmojiEvents />} label="Member Badges" />
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/settings/billing')}
              sx={{
                backgroundColor: COLORS.accent,
                '&:hover': { backgroundColor: COLORS.accent, opacity: 0.9 },
              }}
            >
              Upgrade Now
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Loading state
  if (loadingTerms) {
    return (
      <Box>
        <Box mb={4}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="text" width={300} height={24} />
        </Box>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={4}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="h4" fontWeight="bold">
              Community Hub
            </Typography>
            <Chip
              label={tier}
              size="small"
              sx={{
                textTransform: 'capitalize',
                bgcolor:
                  tier === 'summit'
                    ? '#FEF3C7'
                    : tier === 'rise'
                    ? '#E5E7EB'
                    : '#FED7AA',
                color:
                  tier === 'summit'
                    ? '#92400E'
                    : tier === 'rise'
                    ? '#374151'
                    : '#9A3412',
              }}
            />
          </Box>
          <Typography variant="body1" color="text.secondary">
            Connect, share, and grow with fellow professionals
          </Typography>
        </Box>

        {!isMobile && hasAcceptedTerms && canWriteToHub && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditPost(null);
              setCreatePostModalOpen(true);
            }}
            sx={{
              backgroundColor: COLORS.accent,
              '&:hover': { backgroundColor: COLORS.accent, opacity: 0.9 },
            }}
          >
            New Post
          </Button>
        )}
      </Box>

      {/* Admin Moderation Panel */}
      {isSuperAdmin && (
        <Box mb={4}>
          <AdminModerationPanel />
        </Box>
      )}

      {/* Read-only access info for Trial tier */}
      {isReadOnly && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate('/settings/billing')}
            >
              Upgrade to Launch
            </Button>
          }
        >
          You have read-only access to the Community Hub. Upgrade to Launch tier or higher to create posts and comments.
        </Alert>
      )}

      {/* Terms Not Accepted Warning - show for all users with hub access */}
      {!hasAcceptedTerms && hasHubAccess && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setTermsModalOpen(true)}
            >
              Accept Terms
            </Button>
          }
        >
          Please accept the Community Guidelines to participate in discussions.
        </Alert>
      )}

      {/* Posts Feed */}
      {loadingPosts ? (
        <Box>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
          ))}
        </Box>
      ) : postsError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load posts. Please try again.
        </Alert>
      ) : posts.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Forum sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No posts yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Be the first to start a conversation!
            </Typography>
            {hasAcceptedTerms && canWriteToHub && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setEditPost(null);
                  setCreatePostModalOpen(true);
                }}
                sx={{
                  backgroundColor: COLORS.accent,
                  '&:hover': { backgroundColor: COLORS.accent, opacity: 0.9 },
                }}
              >
                Create First Post
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Box>
          {posts.map((post) => (
            <PostCard
              key={post._id}
              post={post}
              onEdit={handleEditPost}
              onReport={handleReportPost}
              onCommentClick={handleCommentClick}
              onPin={handlePinPost}
              onHide={handleHidePost}
              onUnhide={handleUnhidePost}
              showAdminActions={isSuperAdmin}
            />
          ))}
        </Box>
      )}

      {/* Mobile FAB - only show for users with write access */}
      {isMobile && hasAcceptedTerms && canWriteToHub && (
        <Fab
          color="primary"
          aria-label="create post"
          onClick={() => {
            setEditPost(null);
            setCreatePostModalOpen(true);
          }}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            backgroundColor: COLORS.accent,
            '&:hover': { backgroundColor: COLORS.accent, opacity: 0.9 },
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Modals */}
      <TermsAcceptanceModal
        open={termsModalOpen}
        onAccept={handleTermsAccepted}
        onDecline={handleTermsDeclined}
      />

      <CreatePostModal
        open={createPostModalOpen}
        onClose={() => {
          setCreatePostModalOpen(false);
          setEditPost(null);
        }}
        editPost={editPost}
      />

      <PostDetailModal
        open={!!selectedPost}
        onClose={() => setSelectedPost(null)}
        post={selectedPost}
        onReportComment={handleReportComment}
        onHideComment={handleHideComment}
        showAdminActions={isSuperAdmin}
        canWrite={canWriteToHub}
      />

      <ReportDialog
        open={!!reportTarget}
        onClose={() => setReportTarget(null)}
        target={reportTarget?.target || null}
        type={reportTarget?.type || 'post'}
      />
    </Box>
  );
}
